import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  Mail, Lock, User, Eye, EyeOff,
  ChevronRight, AlertTriangle, CreditCard,
  Moon, Sun
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';
import './DataQualityDetectiveLogin.css';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

// Responsive hook
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );
    };
    checkTouch();
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowSize.width < 900 || (isTouchDevice && windowSize.width < 1024);
  const isTablet = !isMobile && windowSize.width >= 900 && windowSize.width < 1200;
  return { isMobile, isTablet, isTouchDevice, width: windowSize.width, height: windowSize.height };
};

const DataQualityDetectiveLogin = () => {
  const [isLogin, setIsLogin] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('signup') !== '1';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { isMobile } = useResponsive();

  const redirectTo = new URLSearchParams(window.location.search).get('redirect');
  const postAuthPath = redirectTo ? `/${redirectTo}` : '/app';

  // Load Outfit font
  useEffect(() => {
    if (!document.querySelector('link[href*="Outfit"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  const isSubscriptionError = (errorMessage) => {
    if (!errorMessage) return false;
    const keywords = ['trial', 'expired', 'subscribe', 'subscription', 'plan', 'upgrade'];
    return keywords.some(k => errorMessage.toLowerCase().includes(k));
  };

  const isTauri = Boolean(window.__TAURI_INTERNALS__);

  useEffect(() => {
    if (isTauri) return;

    const loadGoogleScript = () => {
      const existingScript = document.querySelector('script[src*="accounts.google.com"]');
      if (existingScript) existingScript.remove();

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => { if (window.google) initializeGoogleSignIn(); };
      script.onerror = () => setError('Failed to load Google Sign-In. Please try again later.');
      document.body.appendChild(script);
    };
    loadGoogleScript();
    return () => {
      const btn = document.querySelector('div[id^="g_signin"]');
      if (btn) btn.remove();
    };
  }, []);

  const initializeGoogleSignIn = () => {
    if (!window.google?.accounts) return;
    try {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(
        document.getElementById('googleSignInDiv'),
        {
          type: 'standard',
          theme: isDarkMode ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: isMobile ? 300 : 360,
        }
      );
    } catch (err) {
      console.error('Error initializing Google Sign-In:', err);
      setError('Failed to initialize Google Sign-In');
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      setLoading(true);
      setError('');
      if (!response?.credential) throw new Error('No credential received from Google');
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const backendResponse = await fetch(`${API_BASE_URL}/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: response.credential, email: payload.email, name: payload.name, picture: payload.picture }),
      });
      const data = await backendResponse.json();
      if (!backendResponse.ok) throw new Error(data.detail || JSON.stringify(data));
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userInfo', JSON.stringify({ name: payload.name, email: payload.email, picture: payload.picture }));
      navigate(postAuthPath);
    } catch (err) {
      setError(typeof err === 'string' ? err : err.message || 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isLogin && password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match. Please re-enter them.');
      return;
    }
    setLoading(true);
    try {
      const endpoint = `${API_BASE_URL}/${isLogin ? 'login' : 'register'}`;
      const body = isLogin ? { email, password } : { email, password, name: fullName.trim() };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Authentication failed');
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userInfo', JSON.stringify({ name: data.name, email: data.email, picture: null }));
      navigate(postAuthPath);
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Something went wrong. Please try again.');
      setForgotSubmitted(true);
    } catch (err) {
      setForgotError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const backToSignIn = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSubmitted(false);
    setForgotError('');
  };

  return (
    <div className="dql-root" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Ambient backdrop */}
      <div className="dql-backdrop" aria-hidden="true">
        <div className="dql-glow dql-glow-1" />
        <div className="dql-glow dql-glow-2" />
      </div>

      <div className="dql-panels">
        <div className="dql-left">
          <div className="dql-form-container">
            {/* Brand */}
            <div className="dql-logo-section">
              <div className="dql-logo-wrapper">
                <img
                  src={isDarkMode ? logoSourceInverted : logoSource}
                  alt="dataQuad"
                  className="dql-logo-image"
                />
              </div>
              <h1 className="dql-brand-title">dataQuad</h1>
              <div className="dql-pill">
                <span className="dql-pill-dot" />
                <span>{isLogin ? 'Secure sign-in' : 'Start your free trial'}</span>
              </div>
            </div>

            {/* Card */}
            <div className="dql-card">
              <div className="dql-card-header">
                <h2 className="dql-card-title">
                  {showForgotPassword
                    ? (forgotSubmitted ? 'Check your email' : 'Reset your password')
                    : (isLogin ? 'Welcome back' : 'Create account')}
                </h2>
                <p className="dql-card-subtitle">
                  {showForgotPassword
                    ? (forgotSubmitted
                        ? "If that email is registered, we've sent a link to reset your password."
                        : "Enter your email and we'll send you a link to reset your password.")
                    : (isLogin
                        ? 'Sign in to continue to your dashboard.'
                        : 'Start your 15-day free trial — no credit card required.')}
                </p>
              </div>

              {showForgotPassword ? (
                <>
                  {forgotError && (
                    <div className="dql-error" role="alert">
                      <AlertTriangle size={16} />
                      <span>{forgotError}</span>
                    </div>
                  )}

                  {forgotSubmitted ? (
                    <div className="dql-switch" style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={backToSignIn}
                        className="dql-btn-submit"
                      >
                        <span>Back to sign in</span>
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPasswordSubmit} className="dql-form">
                      <div className="dql-field">
                        <Mail size={18} className="dql-input-icon" />
                        <input
                          className="dql-input"
                          type="email"
                          placeholder="Email address"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          autoComplete="email"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        className="dql-btn-submit"
                        disabled={forgotLoading}
                      >
                        {forgotLoading ? (
                          <>
                            <div className="dql-spinner" />
                            <span>Sending…</span>
                          </>
                        ) : (
                          <>
                            <span>Send reset link</span>
                            <ChevronRight size={18} />
                          </>
                        )}
                      </button>

                      <div className="dql-switch">
                        <p className="dql-switch-text">
                          Remembered it?
                          <button
                            type="button"
                            onClick={backToSignIn}
                            className="dql-switch-btn"
                          >
                            Back to sign in
                          </button>
                        </p>
                      </div>
                    </form>
                  )}
                </>
              ) : (
                <>
              {error && !isSubscriptionError(error) && (
                <div className="dql-error" role="alert">
                  <AlertTriangle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {error && isSubscriptionError(error) && (
                <div
                  className="dql-sub-error"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/subscription')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/subscription'); }}
                >
                  <div className="dql-sub-icon">
                    <CreditCard size={16} />
                  </div>
                  <div className="dql-sub-content">
                    <div className="dql-sub-text">{error}</div>
                    <div className="dql-sub-link">
                      <span>View subscription plans</span>
                      <ChevronRight size={14} />
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="dql-form">
                {!isLogin && (
                  <div className="dql-field">
                    <User size={18} className="dql-input-icon" />
                    <input
                      className="dql-input"
                      type="text"
                      placeholder="Full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="dql-field">
                  <Mail size={18} className="dql-input-icon" />
                  <input
                    className="dql-input"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="dql-field">
                  <Lock size={18} className="dql-input-icon" />
                  <input
                    className="dql-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isLogin ? 'Password' : 'Password (min. 8 characters)'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    minLength={isLogin ? undefined : 8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="dql-password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {isLogin && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' }}>
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setError(''); }}
                      className="dql-switch-btn"
                      style={{ fontSize: '13px' }}
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {!isLogin && (
                  <>
                    <div className="dql-field">
                      <Lock size={18} className="dql-input-icon" />
                      <input
                        className="dql-input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        required
                        aria-invalid={confirmPassword.length > 0 && confirmPassword !== password}
                        style={
                          confirmPassword.length > 0 && confirmPassword !== password
                            ? { borderColor: '#EF4444' }
                            : undefined
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="dql-password-toggle"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && confirmPassword !== password && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        fontSize: '12.5px', color: '#EF4444',
                        marginTop: '-6px', marginBottom: '2px',
                      }} role="status">
                        <AlertTriangle size={13} />
                        <span>Passwords do not match</span>
                      </div>
                    )}
                  </>
                )}

                <button
                  type="submit"
                  className="dql-btn-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="dql-spinner" />
                      <span>Please wait…</span>
                    </>
                  ) : (
                    <>
                      <span>{isLogin ? 'Sign in' : 'Create account'}</span>
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                {!isTauri && (
                  <>
                    <div className="dql-divider">
                      <div className="dql-divider-line" />
                      <span className="dql-divider-text">or</span>
                      <div className="dql-divider-line" />
                    </div>

                    <div id="googleSignInDiv" className="dql-google" />
                  </>
                )}

                <div className="dql-switch">
                  <p className="dql-switch-text">
                    {isLogin ? "Don't have an account?" : 'Already have an account?'}
                    <button
                      type="button"
                      onClick={() => { setIsLogin(!isLogin); setFullName(''); setConfirmPassword(''); setShowConfirmPassword(false); setError(''); }}
                      className="dql-switch-btn"
                    >
                      {isLogin ? 'Create one' : 'Sign in'}
                    </button>
                  </p>
                </div>
              </form>
                </>
              )}
            </div>

            <div className="dql-footer">
              By continuing, you agree to our{' '}
              <a href="/terms" className="dql-footer-link">Terms of Service</a>{' '}
              and{' '}
              <a href="/privacy" className="dql-footer-link">Privacy Policy</a>.
            </div>

            <button
              onClick={() => {
                const globalToggle = document.querySelector('#global-theme-toggle button');
                if (globalToggle) globalToggle.click();
              }}
              className="dql-theme-toggle"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun size={13} /> : <Moon size={13} />}
              {isDarkMode ? 'Light' : 'Dark'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityDetectiveLogin;